import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { AppLayout } from '@jotta/ui/AppLayout'
import { ErrorPage } from '@jotta/ui/ErrorPage'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { ErrorBoundary } from '@sentry/react'
import { observer } from 'mobx-react-lite'
import type { PropsWithChildren } from 'react'
import { Suspense, useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { PublicHeader } from './PublicHeader'
import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { Header } from './Header'
import { useBrandingInfo, useFeatures } from '@jotta/grpc-connect-client/config'
import { CreateUser } from '../../auth/CreateUser'
import { ModalDialog } from '@jotta/ui/ModalDialog'
import { Trans } from '@lingui/macro'
import { useAuthStore } from '../../auth/useAuthStore'
import { Footer } from '@jotta/ui/Footer'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

function SignupDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  return (
    <ModalDialog open={open} onClose={onClose}>
      <header className="h2 text-center">
        <Trans>Create a free account</Trans>
      </header>
      <CreateUser
        className="p-8"
        type="private"
        allowAppPromotion
        keepRouteOnSuccess
        federationIntent={{
          case: 'signup',
          value: {},
        }}
      />
    </ModalDialog>
  )
}

type WebappSection =
  | 'sharedphotos'
  | 'sharedfiles'
  | 'not_found'
  | 'terms'
  | 'cookies'
  | 'download'
  | 'afterterminate'
  | 'signupaccepted'
  | 'upgradeaccepted'
  | 'paymentstatus'
  | 'support'

function hasSignup(section: WebappSection) {
  switch (section) {
    case 'support':
    case 'not_found':
    case 'terms':
    case 'download':
    case 'cookies':
      return true
    case 'upgradeaccepted':
    case 'afterterminate':
    case 'signupaccepted':
    case 'paymentstatus':
    case 'sharedphotos':
    case 'sharedfiles':
      return false
    default:
      return exhaustiveGuard(section)
  }
}

function hasFooter(section: WebappSection) {
  switch (section) {
    case 'afterterminate':
    case 'signupaccepted':
    case 'paymentstatus':
    case 'support':
    case 'upgradeaccepted':
    case 'not_found':
    case 'terms':
    case 'download':
    case 'cookies':
      return true
    case 'sharedphotos':
    case 'sharedfiles':
      return false
    default:
      return exhaustiveGuard(section)
  }
}

export const PublicLayout = observer<
  PropsWithChildren<{
    section: WebappSection
    className?: string
  }>
>(function WebappLayoutPublic({ children = <Outlet />, section, className }) {
  const { signup } = useFeatures()
  const authenticated = useAuthenticated()
  const { federationToken } = useAuthStore()
  const brandStore = useBrandStore()
  const { signupFreeLink } = useBrandingInfo()
  const [signingUp, setSigningUp] = useState(Boolean(federationToken))

  const startSignup =
    signup && hasSignup(section)
      ? () => {
          if (signupFreeLink) {
            window.location.assign(signupFreeLink)
          } else {
            setSigningUp(true)
          }
        }
      : undefined

  useEffect(() => {
    brandStore.activateElements({
      leftNav: false,
      moduleNav: false,
      timelineScrubber: false,
    })
  }, [brandStore])
  return (
    <AppLayout
      header={
        authenticated === 'TRUE' ? (
          <Header />
        ) : (
          <PublicHeader startSignup={startSignup} />
        )
      }
      footer={hasFooter(section) ? <Footer /> : undefined}
      className={className}
    >
      <ErrorBoundary
        fallback={({ error, resetError }) => (
          <ErrorPage error={error} resetError={resetError} />
        )}
        beforeCapture={scope => {
          const translated =
            document.body.parentElement?.classList.contains('translated-ltr') ||
            false
          return scope
            .addBreadcrumb({
              level: 'info',
              category: 'PublicLayout',
              data: {
                section,
                translated,
              },
            })
            .setTag('section', section)
        }}
      >
        <Suspense fallback={<LoadingOverlay open />}>
          <SignupDialog open={signingUp} onClose={() => setSigningUp(false)} />
          {children}
        </Suspense>
      </ErrorBoundary>
    </AppLayout>
  )
})
